import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Menu, Search } from 'lucide-react';
import UserMenu from './UserMenu';
import NotificationBell from '../notifications/NotificationBell';
import { BRAND } from '../../config/brand';

interface TopbarProps {
  /** Called when the mobile hamburger is tapped — Layout owns the drawer state. */
  onMenuClick?: () => void;
  /** Optional slot for page-level breadcrumb / context (rendered between brand and actions). */
  contextSlot?: React.ReactNode;
}

export default function Topbar({ onMenuClick, contextSlot }: TopbarProps) {
  const { t } = useTranslation();

  return (
    <header
      className="
        sticky top-0 z-40 h-16 shrink-0
        bg-bg-secondary/85 backdrop-blur-lg
        border-b border-border-subtle
      "
    >
      <div className="h-full flex items-center gap-3 px-3 sm:px-4">
        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={onMenuClick}
          aria-label={t('sidebar.openMenu')}
          data-tour="mobile-menu-toggle"
          className="
            md:hidden p-2 -ml-1 rounded-lg text-text-secondary
            hover:text-text-primary hover:bg-bg-tertiary transition-colors
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary
            min-w-[44px] min-h-[44px] flex items-center justify-center
          "
        >
          <Menu className="w-5 h-5" aria-hidden="true" />
        </button>

        {/* Mobile brand (desktop brand lives in sidebar) */}
        <Link
          to="/dashboard"
          className="md:hidden flex items-center gap-2 min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary rounded-md"
        >
          <img src="/logo-mark.svg" alt="" aria-hidden="true" className="w-7 h-7 shrink-0" />
          <span className="font-display font-bold text-base text-text-primary">
            {BRAND.name}
          </span>
        </Link>

        {/* Context slot (page title / breadcrumbs) — desktop only, hidden on mobile to avoid crowding */}
        <div className="hidden md:block min-w-0 flex-1">{contextSlot}</div>

        {/* Right cluster */}
        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          {/* Command palette teaser — visual affordance, no behavior yet */}
          <button
            type="button"
            disabled
            aria-label={t('topbar.search')}
            title={t('topbar.searchHint')}
            className="
              hidden md:inline-flex items-center gap-2 h-9 px-3 rounded-lg
              border border-border-subtle bg-bg-tertiary/60
              text-xs text-text-tertiary
              hover:text-text-secondary hover:border-border-default
              transition-colors cursor-not-allowed opacity-70
            "
          >
            <Search className="w-3.5 h-3.5" aria-hidden="true" />
            <span>{t('topbar.search')}</span>
            <kbd className="ml-2 px-1.5 py-0.5 rounded border border-border-subtle bg-bg-secondary text-[10px] font-mono">
              ⌘K
            </kbd>
          </button>

          <NotificationBell />

          <UserMenu />
        </div>
      </div>
    </header>
  );
}
